import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Popover,
  Modal,
  Backdrop,
  useMediaQuery,
  useTheme,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, CalendarToday } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import { ModalActionButton, ModalCancelButton } from '../../common/Buttons';
import ModalBase from '../../common/modal/ModalBase';
import { DatePickerFooter } from '../../common/modal/ModalElements';
import DatePicker from './DatePicker';
import FieldHelperText from './FieldHelperText';

dayjs.extend(isBetweenPlugin);

interface DateRangePickerProps {
  mode?: 'range' | 'single';
  desktopType?: 'modal' | 'popover';

  // Range mode props
  checkInDate?: string;
  checkOutDate?: string;
  onCheckInChange?: (date: string) => void;
  onCheckOutChange?: (date: string) => void;
  checkInError?: boolean;
  checkOutError?: boolean;
  checkInHelperText?: string;
  checkOutHelperText?: string;
  rangeError?: boolean;
  rangeHelperText?: string;

  // Single mode props
  selectedDate?: string;
  onDateChange?: (date: string) => void;

  // Common props
  title?: string;
  onDone?: () => void;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  fontSize?: string;
  fontFamily?: string;
  fontWeight?: number;
  fontColor?: string;

  // External trigger mode
  showControls?: boolean;
  open?: boolean;
  onClose?: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
}

const DateRangePickerComponent: React.FC<DateRangePickerProps> = ({
  mode = 'range',
  desktopType = 'popover',
  checkInDate = '',
  checkOutDate = '',
  onCheckInChange = () => {},
  onCheckOutChange = () => {},
  checkInError = false,
  checkOutError = false,
  checkInHelperText = '',
  checkOutHelperText = '',
  rangeError = false,
  rangeHelperText = '',
  selectedDate = '',
  onDateChange = () => {},
  onDone,
  title,
  disabled = false,
  minDate,
  maxDate,
  fontSize,
  fontFamily,
  fontWeight,
  fontColor,
  showControls = true,
  open: externalOpen,
  onClose,
  anchorRef,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [currentMonth, setCurrentMonth] = useState<Dayjs>(() =>
    dayjs(selectedDate || checkInDate || undefined)
  );
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [yearRangeStart, setYearRangeStart] = useState(dayjs().year() - 6);

  // Buffered state — calendar selections are held here until committed
  const [bufferCheckIn, setBufferCheckIn] = useState('');
  const [bufferCheckOut, setBufferCheckOut] = useState('');
  const [bufferSelectedDate, setBufferSelectedDate] = useState('');

  const isExternallyControlled = externalOpen !== undefined;
  const isOpen = isExternallyControlled ? externalOpen : Boolean(anchorEl);

  useEffect(() => {
    if (!isExternallyControlled) return;
    if (externalOpen) {
      const anchor = anchorRef?.current ?? wrapperRef.current;
      setAnchorEl(anchor);
    } else {
      setAnchorEl(null);
    }
  }, [externalOpen, isExternallyControlled, anchorRef]);

  // Snapshot props into buffer when picker opens
  useEffect(() => {
    if (isOpen) {
      if (mode === 'single') {
        setBufferSelectedDate(selectedDate);
        setCurrentMonth(dayjs(selectedDate || undefined));
      } else {
        setBufferCheckIn(checkInDate);
        setBufferCheckOut(checkOutDate);
        setCurrentMonth(dayjs(checkInDate || undefined));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Derive dayjs from buffer (used by calendar rendering)
  const bufferCheckInDayjs =
    mode === 'range' && bufferCheckIn ? dayjs(bufferCheckIn) : null;
  const bufferCheckOutDayjs =
    mode === 'range' && bufferCheckOut ? dayjs(bufferCheckOut) : null;
  const bufferSelectedDayjs =
    mode === 'single' && bufferSelectedDate ? dayjs(bufferSelectedDate) : null;

  const handleClosePopover = useCallback(() => {
    setAnchorEl(null);
    onClose?.();
  }, [onClose]);

  // Commit buffered values to parent and close
  const handleDone = useCallback(() => {
    if (mode === 'single') {
      onDateChange(bufferSelectedDate);
    } else {
      onCheckInChange(bufferCheckIn);
      onCheckOutChange(bufferCheckOut);
    }
    handleClosePopover();
    onDone?.();
  }, [
    mode,
    bufferSelectedDate,
    bufferCheckIn,
    bufferCheckOut,
    onDateChange,
    onCheckInChange,
    onCheckOutChange,
    handleClosePopover,
    onDone,
  ]);

  // Discard buffer and close
  const handleCancel = useCallback(() => {
    setBufferCheckIn(checkInDate);
    setBufferCheckOut(checkOutDate);
    setBufferSelectedDate(selectedDate);
    handleClosePopover();
  }, [checkInDate, checkOutDate, selectedDate, handleClosePopover]);

  const handleYearSelectorKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showYearSelector) {
        setShowYearSelector(false);
      }
    },
    [showYearSelector]
  );

  useEffect(() => {
    if (showYearSelector) {
      window.addEventListener('keydown', handleYearSelectorKeyDown);
      return () => {
        window.removeEventListener('keydown', handleYearSelectorKeyDown);
      };
    }
  }, [showYearSelector, handleYearSelectorKeyDown]);

  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!showControls) return;

    const target = event.target as HTMLElement;

    const isCalendarButtonClick = target.closest(
      '[aria-label="Open calendar"]'
    );
    if (isCalendarButtonClick) {
      event.stopPropagation();
      event.preventDefault();
      if (wrapperRef.current) {
        setAnchorEl(wrapperRef.current);
      }
      return;
    }

    if (target.closest('[aria-label="Clear date"]')) {
      return;
    }

    if (isOpen) {
      return;
    }

    if (target.tagName === 'BUTTON') {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
  };

  const isDateInRange = (date: Dayjs): boolean => {
    if (mode !== 'range' || !bufferCheckInDayjs || !bufferCheckOutDayjs)
      return false;
    return date.isBetween(bufferCheckInDayjs, bufferCheckOutDayjs, null, '[]');
  };

  const isDateDisabled = (date: Dayjs): boolean => {
    if (minDate && date.isBefore(dayjs(minDate), 'day')) return true;
    if (maxDate && date.isAfter(dayjs(maxDate), 'day')) return true;
    return false;
  };

  // Date clicks update buffer only — never mutates parent state
  const handleDateClick = (date: Dayjs) => {
    if (isDateDisabled(date)) return;

    const dateStr = date.format('YYYY-MM-DD');

    if (mode === 'single') {
      setBufferSelectedDate(dateStr);
    } else {
      if (!bufferCheckInDayjs) {
        setBufferCheckIn(dateStr);
      } else if (!bufferCheckOutDayjs) {
        if (date.isAfter(bufferCheckInDayjs, 'day')) {
          setBufferCheckOut(dateStr);
        } else {
          setBufferCheckIn(dateStr);
        }
      } else {
        setBufferCheckIn(dateStr);
        setBufferCheckOut('');
      }
    }
  };

  const renderCalendar = (month: Dayjs) => {
    const firstDay = month.startOf('month');
    const lastDay = month.endOf('month');
    const daysInMonth = lastDay.date();
    const startDate = firstDay.day();

    const days: (Dayjs | null)[] = [];
    for (let i = 0; i < startDate; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(month.date(i));
    }

    const weeks: (Dayjs | null)[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    const chevronSxProps = {
      fontSize: '3rem',
      borderRadius: '50%',
      transition: 'transform 0.2s ease',
      '&:hover': {
        transform: 'scale(1.2)',
      },
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.1,
          minWidth: isMobile ? '100%' : '290px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
          }}
        >
          <Box
            onClick={() =>
              !disabled && setCurrentMonth(currentMonth.subtract(1, 'month'))
            }
            role="button"
            aria-label="Previous month"
            sx={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: disabled ? 0.5 : 1,
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: disabled ? 0.5 : 0.7 },
            }}
          >
            <ChevronLeft sx={chevronSxProps} />
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: isMobile ? '2rem' : '1.7rem',
              minWidth: '150px',
              textAlign: 'center',
            }}
          >
            {month.format('MMMM')}{' '}
            <Box
              component="span"
              onClick={() => setShowYearSelector(true)}
              sx={{
                cursor: 'pointer',
                padding: '0.2rem 0.5rem 0.25rem 0.5rem',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: '#ffe',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  color: '#222',
                },
              }}
            >
              {month.format('YYYY')}
            </Box>
          </Typography>

          <Box
            onClick={() =>
              !disabled && setCurrentMonth(currentMonth.add(1, 'month'))
            }
            role="button"
            aria-label="Next month"
            sx={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: disabled ? 0.5 : 1,
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: disabled ? 0.5 : 0.7 },
            }}
          >
            <ChevronRight sx={chevronSxProps} />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.25,
          }}
        >
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((label) => (
            <Box
              key={label}
              sx={{
                textAlign: 'center',
                fontSize: '1.6rem',
                fontWeight: 600,
                color: 'text.secondary',
                py: 0.25,
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
          }}
        >
          {weeks.map((week, weekIdx) =>
            week.map((day, dayIdx) => {
              if (!day) {
                return (
                  <Box
                    key={`empty-${weekIdx}-${dayIdx}`}
                    sx={{ aspectRatio: '1', minHeight: '32px' }}
                  />
                );
              }

              const isDisabled = isDateDisabled(day);

              let isSelected = false;
              let isStart = false;
              let isEnd = false;
              let inRange = false;

              if (mode === 'single') {
                isSelected = !!(
                  bufferSelectedDayjs && day.isSame(bufferSelectedDayjs, 'day')
                );
              } else {
                isStart = !!(
                  bufferCheckInDayjs && day.isSame(bufferCheckInDayjs, 'day')
                );
                isEnd = !!(
                  bufferCheckOutDayjs && day.isSame(bufferCheckOutDayjs, 'day')
                );
                inRange = isDateInRange(day);
              }

              let bgColor = 'transparent';
              let textColor = 'text.primary';
              let cursor = 'pointer';

              if (isDisabled) {
                textColor = 'text.disabled';
                cursor = 'not-allowed';
              } else if (isSelected || isStart || isEnd) {
                bgColor = theme.palette.primary.main;
                textColor = 'white';
              } else if (inRange) {
                bgColor = theme.palette.primary.light;
              }

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  onClick={() => !isDisabled && handleDateClick(day)}
                  role="button"
                  aria-label={day.format('dddd, D MMMM YYYY')}
                  aria-disabled={isDisabled || undefined}
                  sx={{
                    aspectRatio: '1',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: bgColor,
                    color: textColor,
                    borderRadius:
                      mode === 'single'
                        ? isSelected
                          ? '50%'
                          : 0
                        : isStart
                          ? '50% 0 0 50%'
                          : isEnd
                            ? '0 50% 50% 0'
                            : 0,
                    cursor,
                    fontWeight: isSelected || isStart || isEnd ? 700 : 500,
                    transition: 'all 0.2s ease',
                    '&:hover': isDisabled
                      ? {}
                      : {
                          backgroundColor:
                            isSelected || isStart || isEnd
                              ? theme.palette.primary.main
                              : theme.palette.primary.light,
                        },
                  }}
                >
                  {day.date()}
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    );
  };

  // Date summary text (shows buffered selection)
  const dateMask = '...';
  const dateSummary =
    mode === 'single'
      ? bufferSelectedDate
        ? dayjs(bufferSelectedDate).format('DD/MM/YYYY')
        : dateMask
      : bufferCheckIn && bufferCheckOut
        ? `${dayjs(bufferCheckIn).format('DD/MM/YYYY')} - ${dayjs(bufferCheckOut).format('DD/MM/YYYY')}`
        : bufferCheckIn
          ? `${dayjs(bufferCheckIn).format('DD/MM/YYYY')} - ${dateMask}`
          : `${dateMask}`;

  // Day count for range mode
  const dayCount =
    mode === 'range' && bufferCheckIn && bufferCheckOut
      ? dayjs(bufferCheckOut).diff(dayjs(bufferCheckIn), 'day')
      : null;

  // Determine if any selection has changed from the original props
  const hasChanged =
    mode === 'single'
      ? bufferSelectedDate !== selectedDate
      : bufferCheckIn !== checkInDate || bufferCheckOut !== checkOutDate;

  // Shared calendar body — used by both Popover and Modal
  const calendarBody = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        position: 'relative',
      }}
    >
      <Typography
        sx={{
          fontSize: '1.9rem',
          whiteSpace: 'nowrap',
          fontWeight: 700,
          position: 'absolute',
          color:
            mode === 'single'
              ? bufferSelectedDate
                ? theme.palette.secondary.main
                : theme.palette.secondary.main
              : bufferCheckIn && bufferCheckOut
                ? theme.palette.secondary.main
                : theme.palette.secondary.main,
          '@media (min-width: 600px)': {
            fontSize: '2rem',
            fontWeight: 700,
            position: 'relative',
          },
        }}
      >
        {dateSummary}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: isMobile ? 0 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'flex-start',
          width: '100%',
          maxWidth: '600px',
          minWidth: '352px',
          fontSize: isMobile ? '1.8rem' : '1.6rem',
          minHeight: isMobile ? '25rem' : 'auto',
          // justifyContent: 'flex-start',
          justifyContent: isMobile ? 'flex-start' : 'center',
          paddingTop: isMobile ? 6 : 0,
        }}
      >
        {renderCalendar(currentMonth)}
        {!isMobile &&
          mode === 'range' &&
          renderCalendar(currentMonth.add(1, 'month'))}
      </Box>

      <Box
        sx={{
          minHeight: '3.4rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: '2rem', fontWeight: 700 }}>
          {dayCount !== null && `${dayCount} day${dayCount === 1 ? '' : 's'}`}
        </Typography>
      </Box>
    </Box>
  );

  // Popover content — calendar body + action buttons inline
  const popoverContent = (
    <Box
      sx={{
        paddingY: 2,
        paddingX: '0.25rem!important',
        bgcolor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        '@media (min-width: 600px)': { p: 2 },
      }}
    >
      {calendarBody}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          mt: 1,
        }}
      >
        <ModalCancelButton onClick={handleCancel} />
        <ModalActionButton
          onClick={handleDone}
          text="Done"
          disabled={!hasChanged}
        />
      </Box>
    </Box>
  );

  return (
    <Box
      ref={wrapperRef}
      onClickCapture={handleClickCapture}
      sx={
        showControls
          ? {
              width: '100%',
              cursor: disabled ? 'not-allowed' : 'pointer',
              '&:hover': { opacity: disabled ? 1 : 0.95 },
            }
          : { display: 'none' }
      }
    >
      {showControls && (
        <>
          {mode === 'single' ? (
            <Box sx={{ mb: 0 }}>
              <DatePicker
                value={selectedDate}
                onChange={onDateChange}
                error={false}
                disabled={disabled}
                helperText=""
                fontSize={fontSize}
                fontFamily={fontFamily}
                fontWeight={fontWeight}
                fontColor={fontColor}
              />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 1.5 }}>
                <DatePicker
                  value={checkInDate}
                  onChange={onCheckInChange}
                  error={checkInDate ? false : checkInError}
                  disabled={disabled}
                  helperText={checkInDate ? '' : checkInHelperText}
                  fontSize={fontSize}
                  fontFamily={fontFamily}
                  fontWeight={fontWeight}
                  fontColor={fontColor}
                />
              </Box>

              <Box sx={{ mb: rangeError ? 1.5 : 0 }}>
                <DatePicker
                  value={checkOutDate}
                  onChange={onCheckOutChange}
                  error={checkOutDate ? false : checkOutError}
                  disabled={disabled}
                  helperText={checkOutDate ? '' : checkOutHelperText}
                  fontSize={fontSize}
                  fontFamily={fontFamily}
                  fontWeight={fontWeight}
                  fontColor={fontColor}
                />
              </Box>

              {rangeError && rangeHelperText && (
                <Box sx={{ mt: 1 }}>
                  <FieldHelperText error={true} helperText={rangeHelperText} />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Desktop Popover — unchanged layout, now with buffered state */}
      {!isMobile && desktopType === 'popover' && (
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleCancel}
          disableAutoFocus={true}
          disableEnforceFocus={true}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          // No explicit zIndex previously - defaulted to MUI's
          // theme.zIndex.modal (1300). ModalBase's own StyledDialog is
          // explicitly raised to zIndex: 2100 (to beat StickyFooter's
          // 2000), so this popover needs to clear that too, or it renders
          // beneath any ModalBase dialog it's opened from (e.g.
          // AddPaymentModal). Matches CustomSelect's dropdown zIndex.
          sx={{ zIndex: 2200 }}
          PaperProps={{
            sx: {
              borderRadius: '0.5rem',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0, 0, 0, 0.8)'
                  : '0 5px 7px 0 rgba(0, 0, 0, 0.5)',
            },
          }}
        >
          {popoverContent}
        </Popover>
      )}

      {/* Modal — uses ModalBase for blue header, drag, close button, dark backdrop */}
      {(isMobile || desktopType === 'modal') && (
        <ModalBase
          open={isOpen}
          onClose={handleCancel}
          headerIcon={<CalendarToday />}
          headerTitle={
            title || (mode === 'single' ? 'Select Date' : 'Select Date Range')
          }
          desktopWidth={mode === 'range' && !isMobile ? '660px' : '500px'}
          mobileWidth="96vw"
        >
          <Box
            sx={{
              paddingY: isMobile ? '.1rem' : 2,
              paddingX: '0.25rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 0.5,
            }}
          >
            {calendarBody}
          </Box>

          <DatePickerFooter>
            <ModalCancelButton onClick={handleCancel} />
            <ModalActionButton
              onClick={handleDone}
              text="Done"
              disabled={!hasChanged}
            />
          </DatePickerFooter>
        </ModalBase>
      )}

      {/* Year selector sub-modal — unchanged */}
      {showYearSelector && (
        <Modal
          open={showYearSelector}
          onClose={() => setShowYearSelector(false)}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
              sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
            },
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '96%',
              maxWidth: '350px',
              backgroundColor: theme.palette.background.paper,
              borderRadius: '0.2rem',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0, 0, 0, 0.8)'
                  : '0 8px 32px rgba(0, 0, 0, 0.15)',
              p: 1,
              position: 'relative',
              zIndex: theme.zIndex.modal + 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.8rem',
                textAlign: 'center',
                mb: 1,
              }}
            >
              Select Year
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 0.25,
                mb: 2,
              }}
            >
              {Array.from({ length: 12 }, (_, i) => {
                const year = yearRangeStart + i;
                const isCurrentYear = year === currentMonth.year();
                return (
                  <Box
                    key={year}
                    onClick={() => {
                      setCurrentMonth(currentMonth.year(year));
                      setShowYearSelector(false);
                    }}
                    sx={{
                      p: 0.75,
                      textAlign: 'center',
                      fontWeight: isCurrentYear ? 600 : 400,
                      fontSize: '1.8rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: isCurrentYear
                        ? theme.palette.primary.main
                        : 'transparent',
                      color: isCurrentYear ? 'white' : 'inherit',
                      border: `2px solid ${
                        isCurrentYear
                          ? theme.palette.primary.main
                          : 'transparent'
                      }`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: isCurrentYear
                          ? theme.palette.primary.dark
                          : 'rgba(0, 0, 0, 0.05)',
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {year}
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
            >
              <Box
                onClick={() => setYearRangeStart(yearRangeStart - 12)}
                sx={{
                  flex: 1,
                  p: 0.25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '1.6rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(0, 0, 0, 0.25)',
                  transition: 'background-color 0.2s ease',
                  '& svg': { opacity: 0.7 },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    '& svg': { opacity: 1 },
                  },
                }}
              >
                <ChevronLeft sx={{ fontSize: '2.75rem' }} />
              </Box>
              <Box
                onClick={() => setYearRangeStart(yearRangeStart + 12)}
                sx={{
                  flex: 1,
                  p: 0.25,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '1.6rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(0, 0, 0, 0.25)',
                  transition: 'background-color 0.2s ease',
                  '& svg': { opacity: 0.7 },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    '& svg': { opacity: 1 },
                  },
                }}
              >
                <ChevronRight sx={{ fontSize: '2.75rem' }} />
              </Box>
            </Box>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default DateRangePickerComponent;
